import { TimeSlotCard } from "../time-slot-card";
import { View } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { TimeSlot } from "@/types/booking";
import { formatDate } from "@/lib/helpers/date";
import { EmptySlotCard } from "../time-slot-card/empty-slot-card";
import { UpgradePromoCard } from "../time-slot-card/upgrade-promo-card";
import { useMemo } from "react";
import type { PackageDepartmentEnum } from "@/__generated__/graphql";
import { PackageFrequencyGroupEnum } from "@/types/booking";
import type { ServiceName } from "@/types/service";
import { Typography } from "../../typography";
import { useBookingState } from "@/store/booking/useBookingState";

export interface TimeSlotGridProps {
  name: ServiceName;
  loading: boolean;
  selectedSlot: TimeSlot | null;
  timeSlots: Record<string, TimeSlot[]> | null;
  dateRange: Date[];
  department: PackageDepartmentEnum;
  selectedWorkerIds: string[] | null;
  onSelectSlot: (slot: TimeSlot) => void;
  onClickWorkerPortfolio(slot: TimeSlot): void;
  firstNoEmptyIndex: number;
  hidePricing?: boolean;
}

export const TimeSlotGrid = ({
  name,
  dateRange,
  loading,
  timeSlots,
  department,
  selectedSlot,
  selectedWorkerIds,
  onSelectSlot,
  onClickWorkerPortfolio,
  firstNoEmptyIndex,
  hidePricing,
}: TimeSlotGridProps) => {
  const {
    data: { packageDetailsFilter },
  } = useBookingState();
  const isNextDayAdHoc =
    packageDetailsFilter.frontEndFrequency ===
    PackageFrequencyGroupEnum.NEXT_DAY_AD_HOC;
  const allWorkersSlected = useMemo(() => {
    return selectedWorkerIds === null || selectedWorkerIds.length === 0;
  }, [selectedWorkerIds]);

  return (
    <View className="flex flex-col">
      <View
        className={cn(
          "space-around flex flex-row gap-4 border-b border-secondary-border bg-white px-4",
          isNextDayAdHoc ? "w-fit min-[450px]:w-full" : "w-fit",
        )}
      >
        {dateRange.map((date, index) => {
          const formattedDate = formatDate(date, "eee, dd MMM");
          let isPrimaryDate = false;
          if (selectedSlot?.date === formattedDate) {
            isPrimaryDate = true;
          } else if (
            !selectedSlot &&
            firstNoEmptyIndex >= 0 &&
            index === firstNoEmptyIndex
          ) {
            isPrimaryDate = true;
          }
          return (
            <View
              className="flex w-[112px] min-w-[112px] flex-col items-center text-center md:w-[144px] md:min-w-[144px]"
              key={formattedDate}
            >
              <Typography
                variant="caption-md"
                color="foreground-intermediate"
                className="py-3"
              >
                {formattedDate}
              </Typography>
              <View
                className={cn("h-[3px] w-20 rounded-t", {
                  "bg-primary": isPrimaryDate,
                })}
              />
            </View>
          );
        })}
        {isNextDayAdHoc && (
          <View className="w-[120px] min-w-[120px] md:w-[216px] md:min-w-[216px]" />
        )}
      </View>
      <View
        className={cn(
          "relative flex flex-col gap-4 bg-background-intermediate p-4",
          isNextDayAdHoc ? "w-fit md:w-full" : "w-fit",
        )}
      >
        <View className="flex flex-row gap-4">
          {dateRange.map((date) => {
            const formattedDate = formatDate(date);
            if (timeSlots?.[formattedDate]) {
              return (
                <View
                  className="flex w-[112px] min-w-[112px] flex-col gap-2 md:w-[144px] md:min-w-[144px]"
                  key={formattedDate}
                >
                  {timeSlots[formattedDate].map((slot) => {
                    return (
                      <TimeSlotCard
                        name={name}
                        key={slot.id}
                        active={selectedSlot?.id === slot.id}
                        date={slot.date}
                        startTime={slot.startTime}
                        endTime={slot.endTime}
                        onClickWorkerPortfolio={() =>
                          onClickWorkerPortfolio(slot)
                        }
                        onCardClick={() => {
                          if (!loading) {
                            onSelectSlot(slot);
                          }
                        }}
                        department={department}
                        workerName={slot.workerName || ""}
                        avatarUrl={slot.avatarUrl || ""}
                        isCheaper={slot.isCheaper}
                        rateValue={slot.rateValue}
                        discount={slot.discount}
                        workerRating={slot.workerRating || 0}
                        selectedWorkerName={
                          selectedWorkerIds?.includes(slot.workerId)
                            ? slot.workerName
                            : null
                        }
                        hidePricing={hidePricing}
                      />
                    );
                  })}
                </View>
              );
            } else {
              return (
                <View
                  className="w-[112px] min-w-[112px] md:w-[144px] md:min-w-[144px]"
                  key={formattedDate}
                >
                  <EmptySlotCard
                    filtered={!allWorkersSlected}
                    department={department}
                  />
                </View>
              );
            }
          })}
          {isNextDayAdHoc && (
            <View className="w-[120px] min-w-[120px] md:w-[216px] md:min-w-[216px]">
              <UpgradePromoCard name={name} />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};
